import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Upload, X, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SupabaseUploadProps {
  bucket?: string;
  onUploaded: (url: string) => void;
  initialImage?: string;
  className?: string;
}

const BUILD_HASH = `${Date.now()}-${Math.random().toString(36).substring(2)}`;

export const SupabaseUpload: React.FC<SupabaseUploadProps> = ({
  bucket = 'photoalbum',
  onUploaded,
  initialImage,
  className = ''
}) => {
  console.info('[EDITOR BUNDLE LIVE]', BUILD_HASH, new Date().toISOString());
  console.info('[SupabaseUpload] Component mounted with bucket:', bucket);
  
  // Runtime environment check
  console.table({
    SUPABASE_URL: 'https://vjrredluydfrknkcclbw.supabase.co',
    SUPABASE_ANON_KEY: 'eyJhbG***' // Masked
  });

  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(initialImage);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadToSupabase = async (file: File): Promise<string> => {
    console.log('[SupabaseUpload] Starting upload to bucket:', bucket);
    console.log('[SupabaseUpload] File details:', {
      name: file.name,
      type: file.type,
      size: file.size
    });
    
    // Create unique filename
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    // Determine correct MIME type based on file extension as fallback
    const getMimeType = (extension: string) => {
      const mimeTypes: { [key: string]: string } = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'webp': 'image/webp'
      };
      return mimeTypes[extension] || 'image/jpeg';
    };
    
    const contentType = file.type && file.type.startsWith('image/') 
      ? file.type 
      : getMimeType(fileExt || 'jpg');
    
    console.log('[SupabaseUpload] Using content type:', contentType);
    console.log('[SupabaseUpload] Uploading file:', fileName);

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: contentType
      });

    if (error) {
      console.error('[SupabaseUpload] Upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    console.log('[SupabaseUpload] Upload successful:', data);

    // Get public URL
    const { data: publicUrlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(fileName);

    // Ensure the URL has the correct "/object" segment
    const finalUrl = publicUrlData.publicUrl.includes('/object/') 
      ? publicUrlData.publicUrl 
      : publicUrlData.publicUrl.replace('/storage/v1/public/', '/storage/v1/object/public/');
    console.log('[SupabaseUpload] Generated public URL:', finalUrl);

    // Guardrail: Check for blob URLs
    if (finalUrl.startsWith('blob:')) {
      const errorMsg = 'Uploader misconfigured — using blob. Please fix.';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    // Verify it's a proper Supabase Storage URL
    if (!finalUrl.includes('/storage/v1/object/public/')) {
      const errorMsg = 'Invalid upload URL format';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    return finalUrl;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[SupabaseUpload] File selected for upload');
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPG, PNG, GIF or WEBP image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    toast.loading('Uploading image...', { id: 'upload-photo' });

    try {
      const url = await uploadToSupabase(file);
      console.log('[SupabaseUpload] Upload success with URL:', url);
      
      setImageUrl(url);
      onUploaded(url);
      toast.success('Image uploaded successfully', { id: 'upload-photo' });
    } catch (error) {
      console.error('[SupabaseUpload] Upload error:', error);
      toast.error(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { id: 'upload-photo' }
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    console.log('[SupabaseUpload] Removing image');
    setImageUrl(undefined);
    onUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {imageUrl ? (
        <div className="relative inline-block">
          <img
            src={imageUrl}
            alt="Uploaded"
            className="max-h-32 rounded-md object-cover"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 rounded-full h-6 w-6"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload Image
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default SupabaseUpload;